/**
 * Follow-up "Done" with an outcome — web (2026-10-07, Manak). Same box as the
 * AURA Sales app: the lead's recent calls first (pick one → its wrap-up
 * outcome, notes and next date are reused), else "log it without a call"
 * with the call Wrap Up's outcome chips.
 *
 * Backend:
 *   GET  /lead-integrations/lead-imports/:id/recent-calls
 *   POST /lead-integrations/lead-imports/:id/follow-up-done   (My Follow-ups)
 *   POST /telecaller/followups/:id/done                       (Team Follow-ups ✓)
 *   POST /lead-integrations/lead-imports/:id/follow-up-undo { doneEventId }
 */
import { api } from '@/utils/api';

export interface DoneOutcomeOption {
  code: string;
  label: string;
  connected: boolean;
  requiresFollowUp: boolean;
  /** The next follow-up this outcome sets on its own, as of the GET. */
  autoNextFollowUpAt?: string | null;
}

/** The call Wrap Up tiles (phone Disposition.fallbackConnected /
 * fallbackNotConnected). Used when the server list is not available. */
export const WRAP_UP_OUTCOMES: DoneOutcomeOption[] = [
  { code: 'MEETING_SET', label: 'Meeting set', connected: true, requiresFollowUp: true },
  { code: 'NOT_INTERESTED', label: 'Not interested', connected: true, requiresFollowUp: false },
  { code: 'CALLBACK', label: 'Call back', connected: true, requiresFollowUp: true },
  { code: 'IRRELEVANT_OUT_OF_BUDGET', label: 'Irrelevant Lead – Out of Budget', connected: true, requiresFollowUp: false },
  { code: 'NO_ANSWER', label: 'Ringed, no answer', connected: false, requiresFollowUp: true },
  { code: 'BUSY', label: 'Busy', connected: false, requiresFollowUp: true },
  { code: 'SWITCHED_OFF', label: 'Switched off', connected: false, requiresFollowUp: true },
  { code: 'WRONG_NUMBER', label: 'Wrong number', connected: false, requiresFollowUp: false },
  { code: 'DND', label: 'Do not call', connected: false, requiresFollowUp: false },
  { code: 'TRY_LATER', label: 'Try later', connected: false, requiresFollowUp: true },
];

export interface RecentCall {
  id: string;
  at: string;
  direction: 'outgoing' | 'incoming';
  durationSec: number | null;
  connected: boolean;
  agentName: string | null;
  outcome: { code: string; label: string; connected: boolean | null } | null;
  notes: string | null;
  nextFollowUpAt: string | null;
}

export interface RecentCallsPayload {
  calls: RecentCall[];
  defaultCallId: string | null;
  outcomes: DoneOutcomeOption[];
  days: number;
}

export async function fetchRecentCalls(leadId: string): Promise<RecentCallsPayload> {
  const res = await api.get(`/lead-integrations/lead-imports/${leadId}/recent-calls`);
  const d = res.data?.data ?? {};
  return {
    calls: Array.isArray(d.calls) ? d.calls : [],
    defaultCallId: d.defaultCallId ?? null,
    outcomes: Array.isArray(d.outcomes) && d.outcomes.length ? d.outcomes : WRAP_UP_OUTCOMES,
    days: typeof d.days === 'number' ? d.days : 7,
  };
}

// ─── Pure state → what the box shows and sends ──────────────────────────────

/** 2026-10-09 — a sale linked to / proposal tagged on this follow-up
 * (picked from the real Sales Orders / Proposals lists or made in the real
 * New Sales Order form). The server checks it belongs to the lead. */
export interface DoneLink { kind: 'sale' | 'proposal'; id: string; number: string | null; total: number | null }

export interface DoneBoxState {
  calls: RecentCall[];
  outcomes: DoneOutcomeOption[];
  selectedCallId: string | null;
  connected: boolean;
  code: string | null;
  notes: string;
  /** Rep-picked next follow-up (ISO); null = not picked. */
  pickedNext: string | null;
  /** Rep cleared the next follow-up. */
  nextCleared: boolean;
  /** Sale linked / proposal tagged (null = none). */
  link: DoneLink | null;
}

export function initialDoneBoxState(p: { calls: RecentCall[]; defaultCallId: string | null; outcomes?: DoneOutcomeOption[] }): DoneBoxState {
  return {
    calls: p.calls,
    outcomes: p.outcomes?.length ? p.outcomes : WRAP_UP_OUTCOMES,
    selectedCallId: p.defaultCallId,
    connected: true,
    code: null,
    notes: '',
    pickedNext: null,
    nextCleared: false,
    link: null,
  };
}

/** Link (or remove with null). A sale closes the deal → no outcome-made next
 * follow-up; a date the rep picks afterwards still wins. A proposal is still
 * open → the normal next follow-up rules stay. */
export function withLink(s: DoneBoxState, link: DoneLink | null): DoneBoxState {
  return { ...s, link, ...(link?.kind === 'sale' ? { pickedNext: null } : {}) };
}

export function selectedCall(s: DoneBoxState): RecentCall | null {
  return s.calls.find((c) => c.id === s.selectedCallId) ?? null;
}

/** Picking a call: a call without a wrap-up starts from Connected / Not
 * connected read off its duration. */
export function pickCall(s: DoneBoxState, callId: string): DoneBoxState {
  const c = s.calls.find((x) => x.id === callId);
  if (!c) return s;
  return { ...s, selectedCallId: callId, connected: c.outcome?.connected ?? c.connected, code: null, pickedNext: null, nextCleared: false };
}

/** "log it without a call" — tapping the manual area deselects the call. */
export function goManual(s: DoneBoxState): DoneBoxState {
  return s.selectedCallId ? { ...s, selectedCallId: null, code: null, pickedNext: null, nextCleared: false } : s;
}

export function pickOutcome(s: DoneBoxState, code: string): DoneBoxState {
  const o = s.outcomes.find((x) => x.code === code);
  return { ...s, code, connected: o ? o.connected : s.connected, pickedNext: null, nextCleared: false };
}

/** The outcome that will be saved (the call's own, else the chip). */
export function effectiveOutcome(s: DoneBoxState): { code: string; label: string } | null {
  const c = selectedCall(s);
  if (c?.outcome && !s.code) return { code: c.outcome.code, label: c.outcome.label };
  if (!s.code) return null;
  const o = s.outcomes.find((x) => x.code === s.code);
  return { code: s.code, label: o?.label ?? s.code };
}

export type NextSource = 'picked' | 'call' | 'outcome' | 'none';

export function nextFollowUp(s: DoneBoxState, now: Date = new Date()): { at: string | null; source: NextSource; label: string | null } {
  if (s.nextCleared) return { at: null, source: 'none', label: null };
  if (s.pickedNext) return { at: s.pickedNext, source: 'picked', label: null };
  if (s.link?.kind === 'sale') return { at: null, source: 'none', label: null };
  const c = selectedCall(s);
  if (c && c.outcome && !s.code) {
    return c.nextFollowUpAt ? { at: c.nextFollowUpAt, source: 'call', label: null } : { at: null, source: 'none', label: null };
  }
  const o = s.code ? s.outcomes.find((x) => x.code === s.code) : null;
  const auto = o ? (o.autoNextFollowUpAt !== undefined ? o.autoNextFollowUpAt : localAutoNext(o.code, now)) : null;
  if (o && auto) return { at: auto, source: 'outcome', label: o.label };
  return { at: null, source: 'none', label: null };
}

/** Server rule mirror (follow-up-done-outcome.ts autoNextFollowUpAt), used
 * only when the box has no server list (Team row without a lead). */
export function localAutoNext(code: string, now: Date = new Date()): string | null {
  const hours: Record<string, number> = { NO_ANSWER: 4, TRY_LATER: 24, SWITCHED_OFF: 2, BUSY: 2 };
  if (hours[code]) return new Date(now.getTime() + hours[code] * 3600_000).toISOString();
  if (code === 'CALLBACK' || code === 'MEETING_SET') {
    const ist = new Date(now.getTime() + 5.5 * 3600_000);
    return new Date(Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate() + 1, 11, 0) - 5.5 * 3600_000).toISOString();
  }
  return null;
}

/** What blocks Save (null = can save). */
export function saveBlocker(s: DoneBoxState): string | null {
  if (s.link) return null;
  const c = selectedCall(s);
  if (c && !c.outcome && !s.code) return 'Choose how this call went';
  if (!c && !s.code) return 'Pick how it went';
  return null;
}

export function buildDoneBody(s: DoneBoxState, action: 'create_quote' | 'prospect_dealer' | null = null): Record<string, unknown> {
  const c = selectedCall(s);
  const next = nextFollowUp(s);
  const body: Record<string, unknown> = {};
  if (c) body.callId = c.id;
  if (s.code) { body.dispositionCode = s.code; body.connected = s.connected; }
  else if (!c) body.connected = s.connected;
  const notes = s.notes.trim();
  if (notes) body.notes = notes;
  if (next.at) body.nextFollowUpAt = next.at;
  if (action) body.action = action;
  if (!action && s.link) {
    if (s.link.kind === 'sale') { body.action = 'sale_linked'; body.salesOrderId = s.link.id; }
    else { body.action = 'proposal_tagged'; body.proposalId = s.link.id; }
  }
  return body;
}

// ─── Labels ─────────────────────────────────────────────────────────────────

const TIME = new Intl.DateTimeFormat('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
const DAY = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' });
const WDAY = new Intl.DateTimeFormat('en-IN', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' });

function istDayKey(d: Date): string {
  return new Date(d.getTime() + 5.5 * 3600_000).toISOString().slice(0, 10);
}

/** "Today 10:42 am" / "Yesterday 4:15 pm" / "4 Oct 11:05 am" (IST). */
export function callWhen(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  const t = TIME.format(d).toLowerCase();
  const k = istDayKey(d);
  if (k === istDayKey(now)) return `Today ${t}`;
  if (k === istDayKey(new Date(now.getTime() - 86400_000))) return `Yesterday ${t}`;
  return `${DAY.format(d)} ${t}`;
}

/** "3m 12s" / "0s". */
export function callDuration(sec: number | null): string {
  const s = Math.max(0, Math.round(sec ?? 0));
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}

/** "Fri 9 Oct · 11:00 am" for the next follow-up row. */
export function nextWhen(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  const t = TIME.format(d).toLowerCase();
  const k = istDayKey(d);
  if (k === istDayKey(now)) return `Today · ${t}`;
  if (k === istDayKey(new Date(now.getTime() + 86400_000))) return `Tomorrow · ${t}`;
  return `${WDAY.format(d).replace(',', '')} · ${t}`;
}

// ─── Save / undo ────────────────────────────────────────────────────────────

export interface DoneSaved {
  doneEventId: string | null;
  previousFollowUpDate: string | null;
  leadImportId: string | null;
}

export async function saveDone(target: { leadId: string | null; followUpRowId?: string | null }, body: Record<string, unknown>): Promise<DoneSaved> {
  const url = target.followUpRowId
    ? `/telecaller/followups/${target.followUpRowId}/done`
    : `/lead-integrations/lead-imports/${target.leadId}/follow-up-done`;
  const res = await api.post(url, body);
  if (res.data?.success === false) throw new Error(res.data?.error || 'Could not mark the follow-up done');
  const d = res.data?.data ?? {};
  return { doneEventId: d.doneEventId ?? null, previousFollowUpDate: d.previousFollowUpDate ?? null, leadImportId: d.leadImportId ?? target.leadId ?? null };
}

export async function undoDone(leadId: string, doneEventId: string): Promise<void> {
  await api.post(`/lead-integrations/lead-imports/${leadId}/follow-up-undo`, { doneEventId });
}
